import { useMemo, useState } from 'react'
import {
  type BudgetCode,
  type Receipt,
  type Transaction,
} from '@budgetmonkey/shared'
import { Badge, Button, Card, EmptyState, ErrorNote, Money } from '@/components/ui'
import { useCodesById } from '@/features/budgetCodes/hooks'
import { formatDate } from '@/lib/dates'
import { CameraCapture } from './CameraCapture'
import { PendingCaptures } from './PendingCaptures'
import { ReceiptDropZone } from './ReceiptDropZone'
import { ReceiptReview } from './ReceiptReview'
import { ReceiptThumbnail } from './ReceiptThumbnail'
import {
  useChargesByReceipt,
  useSubmittedCycles,
  useDeleteReceipt,
  useOfflineQueue,
  usePurgeReceiptRecord,
  useReceipts,
  useReceiptUploads,
} from './hooks'
import { loadStateOf, LoadingOrOffline } from '@/lib/loadState'
import { humanErrorMessage } from '@/lib/errorMessage'

/**
 * Receipts screen — the capture inbox.
 *
 * §7.1 frames this as a queue of work: everything unlinked is something still
 * to be filed. Extraction lands in P2.4 and the review-and-link flow in P2.5,
 * so for now this proves capture works and nothing is lost.
 */
export function ReceiptsScreen() {
  const receiptsQuery = useReceipts()
  const { data: receipts = [], error, unreadable } = receiptsQuery
  const state = loadStateOf(receiptsQuery)
  const { items, upload, clearFinished, dismiss } = useReceiptUploads()
  const remove = useDeleteReceipt()
  const queue = useOfflineQueue()
  const purge = usePurgeReceiptRecord()
  const [confirmingDelete, setConfirmingDelete] = useState<string>()
  const [reviewingId, setReviewingId] = useState<string>()

  // Filed or not is decided by the charges that actually list the receipt, not
  // by the receipt's own link — that is the side the packet is built from, and
  // it is the side that can name the charge, its budget code and its amount.
  const chargesByReceipt = useChargesByReceipt()
  // Resolved across years, not just the one on screen: a charge moved to
  // last year still points at the code document it was filed against.
  const codesById = useCodesById()

  const chargesFor = (receipt: Receipt): readonly Transaction[] =>
    chargesByReceipt.get(receipt.id ?? '') ?? []

  // Receipts that went out on a submitted packet are done with. They stay
  // reachable a cycle at a time rather than piling up under "Filed", which
  // grows by twenty or so every month and buries the handful still needing
  // attention. Derived from the packets, so nothing has to be archived by hand.
  const submittedCycles = useSubmittedCycles()
  const [openCycleId, setOpenCycleId] = useState<string>()
  const archivedIds = useMemo(() => {
    const all = new Set<string>()
    for (const cycle of submittedCycles) for (const id of cycle.receiptIds) all.add(id)
    return all
  }, [submittedCycles])

  const openCycle = submittedCycles.find((c) => c.periodId === openCycleId)
  const isArchived = (receipt: Receipt) => archivedIds.has(receipt.id ?? '')

  const unlinked = receipts.filter((r) => chargesFor(r).length === 0 && !isArchived(r))
  const linked = receipts.filter((r) => chargesFor(r).length > 0 && !isArchived(r))
  const inOpenCycle =
    openCycle === undefined
      ? []
      : receipts.filter((r) => openCycle.receiptIds.has(r.id ?? ''))

  // Selected by ID from the live list, never held as an object: a receipt
  // captured a moment ago is still being read, and holding the snapshot would
  // freeze it on "Reading…" after the extraction result arrived.
  const reviewing = receipts.find((r) => r.id === reviewingId)

  if (reviewing !== undefined) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <Button variant="ghost" onClick={() => setReviewingId(undefined)}>
            ← All receipts
          </Button>
          {/* "File receipt" over a receipt that is already filed reads as
              work still to do. */}
          <h1 className="text-2xl font-semibold tracking-tight">
            {chargesFor(reviewing).length > 0 ? 'Receipt' : 'File receipt'}
          </h1>
        </div>

        <ReceiptReview
          receipt={reviewing}
          onClose={() => setReviewingId(undefined)}
        />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Receipts</h1>
        <p className="text-sm text-slate-500">
          {receipts.length} captured · {unlinked.length} waiting to be filed
          {archivedIds.size > 0 && ` · ${archivedIds.size} handed in`}
        </p>
      </div>

      {submittedCycles.length > 0 && (
        <div className="flex flex-wrap items-center gap-3">
          <label className="text-sm text-slate-600" htmlFor="cycle">
            Handed in
          </label>
          <select
            id="cycle"
            value={openCycleId ?? ''}
            onChange={(e) => setOpenCycleId(e.target.value === '' ? undefined : e.target.value)}
            className="min-h-11 rounded-lg border border-slate-300 bg-white px-3 text-sm"
          >
            <option value="">Hidden — submitted packets</option>
            {submittedCycles.map((cycle) => (
              <option key={cycle.periodId} value={cycle.periodId}>
                {cycle.label} ({cycle.receiptIds.size})
              </option>
            ))}
          </select>
        </div>
      )}

      {/* §7.1 step 2: the big button, first thing on the screen and reachable
          with a thumb. Hidden where there is no camera — on a laptop it would
          just be a second file picker beside the drop zone. */}
      <div className="sm:hidden">
        <CameraCapture onCapture={upload} />
      </div>

      <PendingCaptures
        pending={queue.pending}
        online={queue.online}
        draining={queue.draining}
        onRetry={() => void queue.drain()}
      />

      <ReceiptDropZone
        items={items}
        onUpload={upload}
        onDismiss={dismiss}
        onClearFinished={clearFinished}
      />

      {error != null && <ErrorNote>{humanErrorMessage(error, "Couldn't load your receipts.")}</ErrorNote>}

      {/* Reported rather than hidden. These captures exist and their files are
          stored; only the record is too broken to display, and §9 does not
          allow one to vanish quietly just because it can't be rendered. */}
      {unreadable.length > 0 && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900">
          <p className="font-medium">
            {unreadable.length === 1
              ? "1 receipt couldn't be displayed"
              : `${unreadable.length} receipts couldn't be displayed`}
          </p>
          <p className="mt-1">
            The stored file is safe — the record itself is malformed. Nothing
            else on this page is affected.
          </p>
          <ul className="mt-2 space-y-1.5 text-xs text-red-800">
            {unreadable.map((problem) => (
              <li key={problem.id} className="flex flex-wrap items-center gap-2">
                <span className="font-mono">
                  {problem.id}: {problem.reason}
                </span>
                <Button
                  variant="ghost"
                  onClick={() => purge.mutate(problem.id)}
                  disabled={purge.isPending}
                >
                  Remove record
                </Button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {state !== 'ready' ? (
        <LoadingOrOffline state={state} what="Your receipts" />
      ) : receipts.length === 0 ? (
        <EmptyState title="No receipts yet">
          <p>Drop a photo or PDF above, or use the file picker.</p>
        </EmptyState>
      ) : (
        <>
          <ReceiptSection
            title={`Waiting to be filed (${unlinked.length})`}
            receipts={unlinked}
            chargesFor={chargesFor}
            codesById={codesById}
            onDelete={(r) => remove.mutate(r)}
            onReview={(r) => setReviewingId(r.id)}
            confirmingDelete={confirmingDelete}
            setConfirmingDelete={setConfirmingDelete}
            deleting={remove.isPending}
            emptyLabel="Everything captured has been filed."
          />

          {openCycle !== undefined && (
            <ReceiptSection
              title={`${openCycle.label} — handed in (${inOpenCycle.length})`}
              receipts={inOpenCycle}
              chargesFor={chargesFor}
              codesById={codesById}
              onDelete={(r) => remove.mutate(r)}
              onReview={(r) => setReviewingId(r.id)}
              confirmingDelete={confirmingDelete}
              setConfirmingDelete={setConfirmingDelete}
              deleting={remove.isPending}
            />
          )}

          {linked.length > 0 && (
            <ReceiptSection
              title={`Filed (${linked.length})`}
              receipts={linked}
              chargesFor={chargesFor}
              codesById={codesById}
              onDelete={(r) => remove.mutate(r)}
              onReview={(r) => setReviewingId(r.id)}
              confirmingDelete={confirmingDelete}
              setConfirmingDelete={setConfirmingDelete}
              deleting={remove.isPending}
            />
          )}
        </>
      )}
    </div>
  )
}

/**
 * Where this capture stands with the reader.
 *
 * A receipt has no `extraction` block between the upload and the function's
 * first write, and older captures never had one at all. Those two look
 * identical in the data, so they're told apart by age — a minute of "Reading…"
 * is honest, an hour of it is a lie.
 */
const READING_GRACE_MS = 3 * 60 * 1000

function ExtractionStatusLine({ receipt }: { receipt: Receipt }) {
  const extraction = receipt.extraction

  if (extraction === null) {
    const age = Date.now() - new Date(receipt.capturedAt).getTime()
    return (
      <p className="text-xs text-slate-500" role="status">
        {age < READING_GRACE_MS ? 'Reading…' : 'Not read — file it by hand'}
      </p>
    )
  }

  if (extraction.status === 'pending') {
    return (
      <p className="text-xs text-slate-500" role="status">
        Reading…
      </p>
    )
  }

  if (extraction.status === 'failed') {
    return <p className="text-xs text-amber-700">Couldn't be read</p>
  }

  return extraction.needsReview ? (
    <p className="text-xs text-amber-700">Needs a check before filing</p>
  ) : (
    <p className="text-xs text-emerald-700">Read cleanly</p>
  )
}

/**
 * The charge or charges this receipt is filed against.
 *
 * The reason this exists: a receipt document has no budget code, no vendor as
 * anyone typed it, and no amount anyone confirmed — all of that lives on the
 * charge. Without it, two copies of the same hotel bill look identical here,
 * and the honest-looking conclusion is that the details need entering again on
 * the receipt. They don't; they are already on the other side of a link this
 * screen simply wasn't showing.
 *
 * Several charges is the normal case for a bill that was split — a hotel that
 * bills the deposit and the balance separately is one piece of paper and two
 * lines on the statement.
 */
function FiledAgainst({
  charges,
  codesById,
}: {
  charges: readonly Transaction[]
  codesById: ReadonlyMap<string, BudgetCode>
}) {
  return (
    <div className="space-y-1 border-t border-slate-100 pt-2">
      <p className="text-xs font-medium uppercase tracking-wide text-slate-400">
        {charges.length === 1 ? 'Filed against' : `Filed against ${charges.length} charges`}
      </p>
      <ul className="space-y-1">
        {charges.map((charge) => {
          const code = codesById.get(charge.budgetCodeId)
          return (
            <li key={charge.id} className="text-xs text-slate-600">
              <span className="font-medium text-slate-900">{charge.vendor}</span> ·{' '}
              <Money value={charge.amount} className="text-xs" /> ·{' '}
              {formatDate(charge.transactionDate)}
              <br />
              <span className="text-slate-500">
                {code === undefined
                  ? 'Budget code missing'
                  : `${code.shortLabel} · ${code.code}`}
              </span>
            </li>
          )
        })}
      </ul>
    </div>
  )
}

function ReceiptSection({
  title,
  receipts,
  chargesFor,
  codesById,
  onDelete,
  onReview,
  confirmingDelete,
  setConfirmingDelete,
  deleting,
  emptyLabel,
}: {
  title: string
  receipts: readonly Receipt[]
  chargesFor: (receipt: Receipt) => readonly Transaction[]
  codesById: ReadonlyMap<string, BudgetCode>
  onDelete: (receipt: Receipt) => void
  onReview: (receipt: Receipt) => void
  confirmingDelete: string | undefined
  setConfirmingDelete: (id: string | undefined) => void
  deleting: boolean
  emptyLabel?: string
}) {
  return (
    <section className="space-y-3">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
        {title}
      </h2>

      {receipts.length === 0 ? (
        emptyLabel !== undefined && (
          <p className="text-sm text-slate-500">{emptyLabel}</p>
        )
      ) : (
        <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {receipts.map((receipt) => (
            // `min-w-0` because a grid item's default `min-width: auto` sizes
            // the track to its widest child. An <iframe> reports an intrinsic
            // 300px and a long merchant string never breaks, so on a phone a
            // card with either would push its column past the screen edge
            // while its neighbours sat correctly — which is exactly how it
            // looked: some right, some running off.
            <li key={receipt.id} className="min-w-0">
              <Card className="space-y-3">
                <ReceiptThumbnail receipt={receipt} />

                <div className="space-y-1">
                  <div className="flex flex-wrap items-center gap-2">
                    {/* Vendor text read off a receipt can be one long
                        unbroken token; it wraps rather than widening the card. */}
                    <span className="min-w-0 break-words text-sm font-medium text-slate-900">
                      {receipt.extractedVendor ?? 'Not read'}
                    </span>
                    {chargesFor(receipt).length === 0 ? (
                      <Badge tone="warn">Unfiled</Badge>
                    ) : (
                      <Badge tone="ok">Filed</Badge>
                    )}
                    {/* §6.1: a forward from an address that isn't trusted is
                        kept, but says so. Dropping it would lose a real
                        receipt the first time a vendor changed sender. */}
                    {!receipt.emailSenderTrusted && (
                      <Badge tone="danger">Unknown sender</Badge>
                    )}
                  </div>

                  {receipt.emailFromAddress !== null && (
                    <p className="truncate text-xs text-slate-500">
                      From {receipt.emailFromAddress}
                      {receipt.emailSubject !== null && ` · ${receipt.emailSubject}`}
                    </p>
                  )}

                  <p className="text-xs text-slate-500">
                    Captured {formatDate(receipt.capturedAt)} ·{' '}
                    {receipt.captureSource.replace(/_/g, ' ')}
                  </p>

                  {receipt.extractedAmount !== null && (
                    <Money value={receipt.extractedAmount} className="text-sm" />
                  )}

                  {/* What the reader made of it only matters while there is
                      still filing to do. Once the receipt is on a charge, the
                      charge is the answer — saying "needs a check before
                      filing" underneath it reads as outstanding work on
                      something already done. */}
                  {chargesFor(receipt).length === 0 ? (
                    <ExtractionStatusLine receipt={receipt} />
                  ) : (
                    <FiledAgainst
                      charges={chargesFor(receipt)}
                      codesById={codesById}
                    />
                  )}
                </div>

                <div className="flex gap-2">
                  {chargesFor(receipt).length === 0 ? (
                    <Button variant="primary" onClick={() => onReview(receipt)}>
                      File it
                    </Button>
                  ) : (
                    <Button onClick={() => onReview(receipt)}>View</Button>
                  )}
                  {confirmingDelete === receipt.id ? (
                    <>
                      <Button
                        variant="danger"
                        onClick={() => {
                          onDelete(receipt)
                          setConfirmingDelete(undefined)
                        }}
                        disabled={deleting}
                      >
                        Delete permanently
                      </Button>
                      <Button
                        variant="ghost"
                        onClick={() => setConfirmingDelete(undefined)}
                      >
                        Cancel
                      </Button>
                    </>
                  ) : (
                    <Button
                      variant="ghost"
                      onClick={() => setConfirmingDelete(receipt.id)}
                    >
                      Delete
                    </Button>
                  )}
                </div>
              </Card>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}
